import { useMemo, useState, useEffect } from 'react';
import { FileSpreadsheet, FileText, Loader2, ChevronDown, ShieldCheck, BarChart3, Building2, Users2, UserSquare2, Wrench, Activity, Clock, ShieldAlert, ArrowLeft } from 'lucide-react';
import type { Ticket } from '@/lib/mock';
import { TEAMS, teamTickets, getTeam } from '@/lib/teams';
import { aggregate, exportExcel, exportPdf } from '@/lib/report';
import { useRole } from '@/lib/role';
import { cn } from '@/lib/utils';

export function ReportsPage({ 
  tickets, 
  now,
  onBack,
  initialTab = 'organization'
}: { 
  tickets: Ticket[]; 
  now: number;
  onBack?: () => void;
  initialTab?: string;
}) {
  const { role, teamId: myTeamId } = useRole();
  const isAdmin = (role === 'Super Admin' || role === 'Admin');
  const [activeTab, setActiveTab] = useState<string>(initialTab);
  const [scope, setScope] = useState<string>(isAdmin ? 'all' : myTeamId ?? TEAMS[0].id);

  // Update scope when tab changes if needed, but typically scope is orthogonal
  useEffect(() => {
    setActiveTab(initialTab);
  }, [initialTab]);

  const scopedTickets = useMemo(() => {
    if (scope === 'all') return tickets;
    const t = getTeam(scope);
    return t ? teamTickets(t, tickets) : [];
  }, [scope, tickets]);

  const scopeLabel = scope === 'all' ? 'All teams' : getTeam(scope)?.name ?? 'Team';
  const agg = useMemo(() => aggregate(scopedTickets, now), [scopedTickets, now]);
  const [busy, setBusy] = useState<null | 'xlsx' | 'pdf'>(null);

  const doExcel = async () => {
    setBusy('xlsx');
    try { await exportExcel(scopedTickets, now, scopeLabel); } finally { setBusy(null); }
  };
  const doPdf = () => {
    setBusy('pdf');
    try { exportPdf(scopedTickets, now, scopeLabel); } finally { setBusy(null); }
  };

  const renderContent = () => {
    if (activeTab === 'organization' || activeTab === 'department' || activeTab === 'team') {
      return (
        <div className="space-y-6">
          <div className="flex flex-wrap items-end justify-between gap-3 border-b border-border pb-4">
            <div>
              <h3 className="font-display text-lg font-medium tracking-tight capitalize">{activeTab} Analytics</h3>
              <p className="mt-1 text-sm text-muted-foreground">
                {scopeLabel} · {agg.total} tickets
                {scope === 'all' && (
                  <span className="ml-2 inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-[11px] text-primary">
                    <ShieldCheck className="h-3 w-3" /> admin
                  </span>
                )}
              </p>
            </div>

            <div className="flex items-center gap-2">
              <div className="relative">
                <select
                  value={scope}
                  onChange={(e) => setScope(e.target.value)}
                  className="h-9 appearance-none rounded-lg border border-input bg-card pl-3 pr-8 text-sm outline-none transition focus:border-primary"
                >
                  {isAdmin && <option value="all">All teams (combined)</option>}
                  {TEAMS.filter((t) => isAdmin || t.id === (myTeamId ?? t.id)).map((t) => (
                    <option key={t.id} value={t.id}>{t.name}</option>
                  ))}
                </select>
                <ChevronDown className="pointer-events-none absolute right-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              </div>

              <button
                onClick={doExcel}
                disabled={!!busy}
                className="inline-flex h-9 items-center gap-2 rounded-lg border border-border bg-card px-3 text-sm font-medium transition hover:bg-muted disabled:opacity-60"
              >
                {busy === 'xlsx' ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileSpreadsheet className="h-4 w-4 text-emerald-600" />}
                Excel
              </button>
              <button
                onClick={doPdf}
                disabled={!!busy}
                className="inline-flex h-9 items-center gap-2 rounded-lg bg-primary px-3 text-sm font-medium text-primary-foreground transition hover:opacity-90 disabled:opacity-60"
              >
                {busy === 'pdf' ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />}
                PDF
              </button>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
            <Kpi label="Total" value={agg.total} />
            <Kpi label="Open" value={agg.open} accent="text-primary" />
            <Kpi label="Breached" value={agg.breached} accent="text-temp-ember" />
            <Kpi label="At risk" value={agg.atRisk} accent="text-temp-warm" />
            <Kpi label="On track" value={agg.onTrack} accent="text-emerald-500" />
          </div>

          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <Breakdown title="By team" rows={agg.byTeam} />
            <Breakdown title="By status" rows={agg.byStatus} />
            <Breakdown title="By priority" rows={agg.byPriority} />
            <Breakdown title="By category" rows={agg.byCategory} />
          </div>
        </div>
      );
    }

    return (
      <div className="flex flex-col items-center justify-center p-12 text-center rounded-xl border border-border bg-card shadow-sm h-[400px]">
        <div className="mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-muted text-muted-foreground">
          <BarChart3 className="h-10 w-10" />
        </div>
        <h3 className="font-display text-xl font-medium capitalize">{activeTab.replace('-', ' ')} Reports</h3>
        <p className="mt-2 text-sm text-muted-foreground max-w-sm">
          Detailed metrics and customizable visualizations for {activeTab.replace('-', ' ')} will be available in the next platform update.
        </p>
      </div>
    );
  };

  return (
    <div className="flex h-full flex-col">
      <div className="flex items-center gap-3 border-b border-border bg-card/60 px-6 py-4">
        {onBack && (
          <button
            onClick={onBack}
            className="flex h-8 w-8 items-center justify-center rounded-lg border border-border text-muted-foreground transition hover:bg-muted hover:text-foreground mr-2"
          >
            <ArrowLeft className="h-4 w-4" />
          </button>
        )}
        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-indigo-500/10 text-indigo-500">
          <BarChart3 className="h-5 w-5" />
        </div>
        <div>
          <h1 className="font-display text-lg font-semibold tracking-tight">Reports & Analytics</h1>
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            Comprehensive system metrics and performance data.
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-hidden flex flex-col md:flex-row">
        <div className="w-full md:w-64 border-r border-border bg-muted/10 p-4 space-y-1 overflow-y-auto">
          <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-2 mt-2 px-2">Operational Reports</div>
          {[
            { id: 'organization', label: 'Organization Reports', icon: Building2 },
            { id: 'department', label: 'Department Reports', icon: Users2 },
            { id: 'team', label: 'Team Reports', icon: UserSquare2 },
            { id: 'technician', label: 'Technician Reports', icon: Wrench },
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={cn(
                "flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-sm font-medium transition",
                activeTab === tab.id ? "bg-primary/10 text-primary" : "text-muted-foreground hover:bg-muted hover:text-foreground"
              )}
            >
              <tab.icon className="h-4 w-4" />
              {tab.label}
            </button>
          ))}

          <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-2 mt-6 px-2">Compliance & Security</div>
          {[
            { id: 'user', label: 'User Activity Reports', icon: Activity },
            { id: 'sla', label: 'SLA Reports', icon: Clock },
            { id: 'audit', label: 'Audit Reports', icon: ShieldAlert },
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={cn(
                "flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-sm font-medium transition",
                activeTab === tab.id ? "bg-primary/10 text-primary" : "text-muted-foreground hover:bg-muted hover:text-foreground"
              )}
            >
              <tab.icon className="h-4 w-4" />
              {tab.label}
            </button>
          ))}
        </div>

        <div className="flex-1 overflow-y-auto bg-muted/5 p-4 sm:p-6 lg:p-8">
          <div className="mx-auto w-full max-w-6xl">
            {renderContent()}
          </div>
        </div>
      </div>
    </div>
  );
}

function Kpi({ label, value, accent }: { label: string; value: number; accent?: string }) {
  return (
    <div className="rounded-xl border border-border bg-card p-4 shadow-sm">
      <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className={`mt-1 font-mono text-2xl font-medium ${accent ?? ''}`}>{value}</div>
    </div>
  );
}

function Breakdown({ title, rows }: { title: string; rows: [string, number][] }) {
  const max = Math.max(...rows.map((r) => r[1]), 1);
  const total = rows.reduce((a, b) => a + b[1], 0) || 1;
  return (
    <div className="rounded-xl border border-border bg-card p-4 shadow-sm">
      <h2 className="mb-3 text-[13px] font-semibold tracking-tight">{title}</h2>
      {rows.length === 0 ? (
        <p className="py-4 text-center text-sm text-muted-foreground">No data.</p>
      ) : (
        <div className="space-y-2.5">
          {rows.slice(0, 8).map(([label, v]) => (
            <div key={label}>
              <div className="mb-1 flex justify-between text-xs">
                <span className="truncate">{label}</span>
                <span className="font-mono text-muted-foreground">{v} · {Math.round((v / total) * 100)}%</span>
              </div>
              <div className="h-1.5 overflow-hidden rounded-full bg-muted">
                <div className="h-full rounded-full bg-primary" style={{ width: `${(v / max) * 100}%` }} />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
